import { UseMutationOptions, useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import Auth from '~models/auth'
import SamsungSmartThingsAuthRepository from '~repositories/impl/samsung.smart-things/auth-repository'
import getMetadata from '~utils/app/get-metadata'
import setMetadata from '~utils/app/set-metadata'
import Mixpanel from '~utils/mixpanel'
import Sentry from '~utils/sentry'
import { markOtpSpent, restoreOtp } from '~utils/app/spent-otp'
import { clearToken, setToken } from '~utils/token'

type OtpLoginParams = Parameters<typeof SamsungSmartThingsAuthRepository.otpLogin>[0]

const useLogInOTP = (
  options?: UseMutationOptions<Auth.LogIn.Response, AxiosError, OtpLoginParams>
) => {
  return useMutation<Auth.LogIn.Response, AxiosError, OtpLoginParams>({
    ...options,
    mutationFn: SamsungSmartThingsAuthRepository.otpLogin,
    onMutate: variables => {
      // 요청을 보내기 전에 소비 처리한다. 응답을 기다리는 동안 웹뷰가 재생성되면 이후
      // 콜백이 실행되지 않아, 사후 정리로는 재사용을 막을 수 없다.
      markOtpSpent(variables.otp)

      // 아직 쓰지 않은 OTP 로만 여기까지 온다 = ST 가 새 세션을 요청한 것이다.
      // 이전 계정 세션을 먼저 비운다. 남겨두면 (a) 이 요청이 이전 계정의 Authorization
      // 헤더를 달고 나가고, (b) 로그인이 실패했을 때 사용자가 이전 계정으로 로그인된 채
      // 남는다. 원래 validateSearch 가 하던 일을 "실제로 새 세션을 만들려는 순간" 으로
      // 옮긴 것이다 — 소비된 OTP 로 재진입할 때는 여기까지 오지 않는다.
      clearToken()

      return options?.onMutate?.(variables)
    },
    onError: (error, req, ctx) => {
      options?.onError?.(error, req, ctx)

      // 응답이 아예 없으면(네트워크 단절 등) 서버가 OTP 를 소비했을 리 없다. 이때만
      // 표식을 되돌려 다음 진입에서 재시도할 수 있게 한다. 응답이 온 경우(401/429/5xx)는
      // 이미 소비됐거나 재시도해도 결과가 같으므로 표식을 유지한다.
      if (!error.response) {
        restoreOtp(req.otp)
      }

      // 인터셉터가 모든 API 에러를 'API Response Error' 로 보내지만 어느 플로우인지
      // 구분되지 않는다. 소비된 OTP 재사용(401)과 브루트포스 잠금(429)을 갈라 보려면
      // 전용 스코프가 필요하다.
      Sentry.captureException(error, {
        tags: { flow: 'samsung.smart-things/log-in-otp' },
        extra: {
          status: error.response?.status,
          reason: error.response?.data,
        },
      })
    },
    onSuccess: (res, req, ctx) => {
      options?.onSuccess?.(res, req, ctx)
      setToken(res.access_token)
      Mixpanel.identify(res.owner.id)

      if (res.is_first_login) {
        Mixpanel.logEvent('sign_up: completed', {
          account_type: 'otp',
        })
      }

      Mixpanel.logEvent('account: logged_in', {
        account_type: 'otp',
      })
    },
    onSettled: (data, error, variables, context) => {
      options?.onSettled?.(data, error, variables, context)
      const originMetadata = JSON.parse(getMetadata() || '{}')
      setMetadata({ ...originMetadata, otp: undefined })
    },
  })
}

export default useLogInOTP
